import { Button } from "../../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { FileIcon, FileX2, MoreHorizontal, Trash2 } from "lucide-react";
import { useAudit } from "../../../../contexts/auditContext";
import { useSessionAnalyticsOwner } from "../../../../hooks/audit/useSessionAnalyticsOwner";
import { DocumentStatus } from "./DocumentStatus";
import { useDocumentManagement } from "../../../../hooks/useDocumentManagement";
import { useCallback, useMemo, useState } from "react";
import { AuditSession } from "../../../../models/audit/auditSession";
import { useProcedureSessionTableShell } from "../../../../hooks/audit/useProcedureSessionTableShell";
import { Input } from "../../../../components/ui/input";
import { getFileInputAcceptString } from "../../../../lib/fileTypes";
import { usePresignedUpload } from "../../../../hooks/usePresignedUpload";
import { UploadProgressOverlay } from "../../../../components/UploadProgressOverlay";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { validateFilesWithToast } from "../../../../lib/fileUploadValidation";
import { isDerivedChildDocument } from "../../../../lib/documentChildTypes";
import { AnalyticsParametersCell } from "../../../../components/analytics/AnalyticsParametersCell";
import { AnalyticsInputToggle } from "../../../../components/analytics/AnalyticsInputToggle";
import { AnalyticsInputRefusalsNote } from "../../../../components/analytics/AnalyticsInputRefusalsNote";
import { SampleReachCount } from "../../../../components/analytics/SampleReachCount";
import { SampleRowInputRow } from "../../../../components/analytics/SampleRowInputRow";
import {
  useDocumentRowAnalytics,
  type DocumentRowAnalytics,
} from "../../../../hooks/audit/useDocumentRowAnalytics";
import {
  ANALYTICS_INPUT_COPY,
  INPUT_BINDING_COPY,
  SAMPLE_REACH_COPY,
  documentKey,
  documentNamesById,
  hasSampleReachColumn,
  type BoundDocumentId,
  type TestTableSourceRow,
} from "../../../../lib/analyticsFeeds";

/**
 * The `For Analytics Test`, `Workflow Parameters` and `Samples Matched` cells,
 * in the order this table reads them. The first two keep the order the Documents
 * tab draws them in, so the pair cannot read one way on one tab and another way
 * on the other.
 *
 * One definition for every row that renders them, so they cannot end up in a
 * different order — or with a different document id — on one row and not
 * another. The components themselves are the Documents tab's own, so no column
 * can drift between the two tabs.
 *
 * The count takes a column here and a line under the control there because this
 * table has no sample column of its own: it is scoped to one sample, and the
 * count is about all of them.
 *
 * It follows the two decisions rather than leading them. Coverage is a fact
 * about a file that feeds a column, so a count read before the flag and the
 * binding is a number about nothing yet.
 */
function AnalyticsCells({
  documentId,
  documentName,
  isAnalyticsInput,
  flagKnown,
  documentFileType,
  analytics,
  documentNames,
  sourceRows,
}: {
  documentId: BoundDocumentId;
  documentName: string;
  isAnalyticsInput: boolean | undefined;
  /**
   * Whether the flag was read at all — the session document behind it found,
   * and the field on it present. The record is read out of a map whose query
   * this table's render gate does not cover, so a row can draw before — or
   * without — the record it describes; and the record itself may carry no flag,
   * because the projection that builds it need not send one. Neither absence
   * says anything about the file, and both look like `false` here.
   */
  flagKnown: boolean;
  documentFileType: string | null | undefined;
  analytics: DocumentRowAnalytics;
  documentNames: ReadonlyMap<BoundDocumentId, string>;
  /** Every sample of the test table, which is the grain the count reads at. */
  sourceRows: readonly TestTableSourceRow[];
}) {
  return (
    <>
      {/* The row selects a document on click, and the switch is a control the
          auditor operates without leaving the row they are on. */}
      <TableCell
        className="border-b"
        onClick={(event) => event.stopPropagation()}
      >
        <AnalyticsInputToggle
          flagged={isAnalyticsInput === true}
          flagKnown={flagKnown}
          documentName={documentName}
          documentFileType={documentFileType}
          pending={analytics.isPendingFor(documentId)}
          bindingWriteOpen={analytics.pending}
          heldReason={analytics.writesHeldReason}
          onChange={(next) => analytics.onSetAnalyticsInput(documentId, next)}
        />
      </TableCell>
      {/* `p-2` because the cell supplies its own padding inside, which would
          otherwise sit on top of the table's. */}
      <TableCell className="border-b p-2">
        <AnalyticsParametersCell
          documentId={documentId}
          documentName={documentName}
          isAnalyticsInput={isAnalyticsInput}
          targets={analytics.targets}
          documentNames={documentNames}
          feedsState={analytics.feedsState}
          deletedColumnNames={analytics.deletedColumnNames}
          pending={analytics.pending}
          writesHeldReason={analytics.writesHeldReason}
          onAssign={analytics.onAssign}
          onClear={analytics.onClear}
        />
      </TableCell>
      {hasSampleReachColumn(sourceRows) && (
        <TableCell className="border-b">
          <SampleReachCount
            sourceRows={sourceRows}
            documentId={documentId}
            targets={analytics.targets}
            flagged={isAnalyticsInput === true}
            hasMatchEditor={false}
          />
        </TableCell>
      )}
    </>
  );
}

/**
 * The analytics columns on a row with no document behind them.
 *
 * Blank rather than absent, so the row keeps its alignment with the headings and
 * the columns stay one question read down the table. The count's cell comes and
 * goes with the column, on the same condition its heading does.
 */
function EmptyAnalyticsCells({
  sourceRows,
}: {
  sourceRows: readonly TestTableSourceRow[];
}) {
  return (
    <>
      <TableCell className="border-b" />
      <TableCell className="border-b" />
      {hasSampleReachColumn(sourceRows) && <TableCell className="border-b" />}
    </>
  );
}

export const AuditDocumentVerification = ({
  procedureSessionId,
  controlSessionId,
  auditSourceRowId,
  auditSession,
}: {
  procedureSessionId: string;
  controlSessionId: string;
  auditSourceRowId: string;
  auditSession: AuditSession;
}) => {
  const { updateCurrentDocument, uploadDocumentsToAuditSession } = useAudit();
  const {
    state: presignedUploadState,
    uploadFiles: uploadFilesPresigned,
    cancel: cancelPresignedUpload,
    reset: resetPresignedUpload,
  } = usePresignedUpload();
  const [isUploading, setIsUploading] = useState(false);
  const {
    documentIdToDocumentMap,
    currentSourceRow,
    localSelectedDocument,
    setLocalSelectedDocument,
    getOrderedDocuments,
    updateDocumentStatus,
    selectNextDocument,
    removeDocument,
  } = useDocumentManagement(
    procedureSessionId,
    controlSessionId,
    auditSourceRowId,
  );
  // Source the full procedure (with `requiredDocuments`) from the table-shell
  // hook — the slim list envelope doesn't carry it, and `useDocumentManagement`
  // pulls the same shape internally so the cache entry is shared.
  const { data: tableShell } =
    useProcedureSessionTableShell(procedureSessionId);
  const procedure = tableShell?.procedure;
  // The analytics columns render only for a procedure that declares an
  // analytics column. This table is shared by every sample and by the general
  // basis, so the absent case is the one that must not move.
  const analyticsOwner = useSessionAnalyticsOwner();
  const analytics = useDocumentRowAnalytics(
    procedureSessionId,
    procedure,
    analyticsOwner,
  );

  // A binding stores a document id and no name, so the editor's account of what
  // fills each input is resolved against the session's own documents.
  const documentNames = useMemo(
    () => documentNamesById(auditSession.documents),
    [auditSession.documents],
  );

  const unassignedDocuments = useMemo(
    () =>
      currentSourceRow?.matchItems
        .filter((item) => {
          if (item.assignments.length !== 0) return false;
          const docEntry = documentIdToDocumentMap?.[item.sourceDocument.id];
          const hasHumanAssignment =
            !!docEntry?.fetchableDocument?.documentAssignments?.length;
          return !hasHumanAssignment;
        })
        .filter(
          (doc, index, self) =>
            index ===
            self.findIndex(
              (d) => d.sourceDocument.id === doc.sourceDocument.id,
            ),
        )
        .filter((doc) => {
          const fetchable =
            documentIdToDocumentMap?.[doc.sourceDocument.id]?.fetchableDocument;
          return fetchable != null && !isDerivedChildDocument(fetchable);
        }) ?? [],
    [currentSourceRow, documentIdToDocumentMap],
  );

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      try {
        // Try presigned upload first (for large files)
        const result = await uploadFilesPresigned(files);

        if (result.usedPresigned) {
          // Presigned upload was used - finalize with upload IDs
          await uploadDocumentsToAuditSession(
            auditSession,
            files,
            auditSourceRowId,
            result.uploadIds,
          );
          resetPresignedUpload();
        } else {
          // Small files - use existing multipart flow
          await uploadDocumentsToAuditSession(
            auditSession,
            files,
            auditSourceRowId,
          );
        }
      } catch (error) {
        // Don't show error for cancelled uploads
        if (error instanceof DOMException && error.name === "AbortError") {
          resetPresignedUpload();
          return;
        }
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [
      auditSession,
      auditSourceRowId,
      uploadDocumentsToAuditSession,
      uploadFilesPresigned,
      resetPresignedUpload,
    ],
  );

  // First-click into a sample: `useSourceRow` and `useProcedureSessionTableShell`
  // may still be in flight. Render a spinner instead of an empty/broken table.
  if (!currentSourceRow || !procedure) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="border-x">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="border-b">Type</TableHead>
              <TableHead className="border-b">Title</TableHead>
              {analytics !== undefined && (
                <>
                  {/* The helper sits on the heading rather than in every row: it
                      describes the column, and one sentence per document is the
                      same sentence N times. */}
                  <TableHead
                    className="border-b"
                    title={ANALYTICS_INPUT_COPY.flagHelper}
                  >
                    {ANALYTICS_INPUT_COPY.flagColumnHeading}
                  </TableHead>
                  <TableHead className="border-b">
                    {INPUT_BINDING_COPY.parametersColumnHeading}
                  </TableHead>
                  {hasSampleReachColumn(auditSession.sourceRows) && (
                    <TableHead className="border-b">
                      {SAMPLE_REACH_COPY.columnHeading}
                    </TableHead>
                  )}
                </>
              )}
              <TableHead className="border-b">Document Match</TableHead>
              <TableHead className="border-b w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {analytics !== undefined &&
              auditSession.procedure.procedureBasis === "sample" && (
                <SampleRowInputRow
                  analytics={analytics}
                  documentNames={documentNames}
                  sourceRows={auditSession.sourceRows}
                />
              )}
            {procedure.requiredDocuments.map((proc) => {
              const docs = getOrderedDocuments
                .filter(
                  (d) =>
                    documentIdToDocumentMap?.[
                      d.sourceDocument.id
                    ]?.fetchableDocument?.documentAssignments?.some(
                      (a) => a.id === proc.id,
                    ) ||
                    d.assignments.some(
                      (a) => a.procedureDocumentId === proc.id,
                    ),
                )
                .filter(
                  (doc, index, self) =>
                    index ===
                    self.findIndex(
                      (d) => d.sourceDocument.id === doc.sourceDocument.id,
                    ),
                )
                .filter((doc) => {
                  const fetchable =
                    documentIdToDocumentMap?.[doc.sourceDocument.id]
                      ?.fetchableDocument;
                  return (
                    fetchable != null && !isDerivedChildDocument(fetchable)
                  );
                });

              const isDocumentMissing =
                docs.length === 0 ||
                (docs.length === 1 && docs[0].state === "rejected");
              const documentAction =
                currentSourceRow?.requiredDocumentsActions.find(
                  (action) => action.procedureDocumentId === proc.id,
                );
              const isMarkedMissing = documentAction?.action === "mark_missing";

              if (isDocumentMissing) {
                return (
                  <TableRow
                    key={proc.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setLocalSelectedDocument({
                        type: proc.id,
                        fileType: "",
                        documentId: "",
                      });
                      updateCurrentDocument({
                        id: "",
                        type: proc.id,
                        fileType: undefined,
                        documentId: "",
                        fileName: undefined,
                        procedureSessionId,
                      });
                    }}
                  >
                    <TableCell className="border-b">{proc.shortName}</TableCell>
                    <TableCell className="border-b text-sm">
                      <div className="flex flex-row gap-2 items-center">
                        <FileX2 className="w-4 h-4" />
                        <div>Document Not Found</div>
                      </div>
                    </TableCell>
                    {/* No file, so no analytics cell has one to answer for. */}
                    {analytics !== undefined && (
                      <EmptyAnalyticsCells
                        sourceRows={auditSession.sourceRows}
                      />
                    )}
                    <TableCell className="border-b">
                      <DocumentStatus isNotRequired={isMarkedMissing} />
                    </TableCell>
                    <TableCell className="border-b"></TableCell>
                  </TableRow>
                );
              }

              return docs.map((doc) => {
                if (doc.state === "rejected") {
                  return null;
                }

                const documentEntry =
                  documentIdToDocumentMap?.[doc.sourceDocument.id];
                const documentData = {
                  id: documentEntry?.id ?? "",
                  documentId: doc.sourceDocument.id,
                  type: proc.id,
                  fileType: doc.sourceDocument.fileType,
                };
                // Both halves handed over together, so this row keys a binding
                // the same way the name map keyed it.
                const boundDocumentId = documentKey(documentData);

                return (
                  <TableRow
                    key={`${proc.id}-${doc.sourceDocument.id}`}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      localSelectedDocument?.documentId ===
                      doc.sourceDocument.id
                        ? "bg-gray-50"
                        : ""
                    }`}
                    onClick={() => {
                      setLocalSelectedDocument({
                        ...documentData,
                      });
                      updateCurrentDocument({
                        ...documentData,
                        procedureSessionId,
                      });
                    }}
                  >
                    <TableCell className="border-b">{proc.shortName}</TableCell>
                    <TableCell className="border-b text-sm">
                      <div className="inline-flex items-center">
                        <FileIcon className="w-4 h-4 mr-2 shrink-0" />
                        <span className="truncate max-w-[200px]">
                          {doc.sourceDocument.fileName ?? "Document not found"}
                        </span>
                      </div>
                    </TableCell>

                    {analytics !== undefined &&
                      (boundDocumentId === null ? (
                        <EmptyAnalyticsCells
                          sourceRows={auditSession.sourceRows}
                        />
                      ) : (
                        <AnalyticsCells
                          documentId={boundDocumentId}
                          documentName={
                            doc.sourceDocument.fileName ?? doc.sourceDocument.id
                          }
                          isAnalyticsInput={documentEntry?.isAnalyticsInput}
                          flagKnown={
                            documentEntry?.isAnalyticsInput !== undefined
                          }
                          documentFileType={
                            documentEntry?.fetchableDocument?.fileType
                          }
                          analytics={analytics}
                          documentNames={documentNames}
                          sourceRows={auditSession.sourceRows}
                        />
                      ))}

                    <TableCell className="border-b">
                      <DocumentStatus
                        state={doc.state}
                        onConfirm={() => {
                          updateDocumentStatus(
                            doc.sourceDocument.id,
                            "approved",
                          );
                          selectNextDocument(doc.sourceDocument.id);
                        }}
                      />
                    </TableCell>
                    <TableCell
                      className="border-b"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              removeDocument(doc.sourceDocument.id)
                            }
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Document From Sample
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              });
            })}
            {unassignedDocuments &&
              unassignedDocuments.length > 0 &&
              unassignedDocuments.map((doc) => {
                const documentEntry =
                  documentIdToDocumentMap?.[doc.sourceDocument.id];
                // Both halves handed over together, so this row keys a binding
                // the same way the name map keyed it.
                const boundDocumentId = documentKey({
                  id: documentEntry?.id ?? "",
                  documentId: doc.sourceDocument.id,
                });
                return (
                  <TableRow
                    key={doc.sourceDocument.id}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      localSelectedDocument?.documentId ===
                      doc.sourceDocument.id
                        ? "bg-gray-50"
                        : ""
                    }`}
                    onClick={() => {
                      setLocalSelectedDocument({
                        type: doc.sourceDocument.id,
                        fileType: doc.sourceDocument.fileType,
                        documentId: doc.sourceDocument.id,
                      });
                      updateCurrentDocument({
                        id: documentEntry?.id ?? "",
                        type: doc.sourceDocument.id,
                        fileType: doc.sourceDocument.fileType,
                        documentId: doc.sourceDocument.id,
                        fileName: doc.sourceDocument.fileName,
                        procedureSessionId,
                      });
                    }}
                  >
                    <TableCell className="border-b">Other</TableCell>
                    <TableCell className="border-b">
                      <div className="flex flex-row gap-2 items-center">
                        <FileIcon className="w-4 h-4 mr-2 shrink-0" />
                        <span className="truncate max-w-[200px]">
                          {doc.sourceDocument.fileName ?? "Document not found"}
                        </span>
                      </div>
                    </TableCell>
                    {analytics !== undefined &&
                      (boundDocumentId === null ? (
                        <EmptyAnalyticsCells
                          sourceRows={auditSession.sourceRows}
                        />
                      ) : (
                        <AnalyticsCells
                          documentId={boundDocumentId}
                          documentName={
                            doc.sourceDocument.fileName ?? doc.sourceDocument.id
                          }
                          isAnalyticsInput={documentEntry?.isAnalyticsInput}
                          flagKnown={
                            documentEntry?.isAnalyticsInput !== undefined
                          }
                          documentFileType={
                            documentEntry?.fetchableDocument?.fileType
                          }
                          analytics={analytics}
                          documentNames={documentNames}
                          sourceRows={auditSession.sourceRows}
                        />
                      ))}
                    <TableCell className="border-b">
                      <DocumentStatus
                        state={doc.state}
                        onConfirm={() => {
                          updateDocumentStatus(
                            doc.sourceDocument.id,
                            "approved",
                          );
                          selectNextDocument(doc.sourceDocument.id);
                        }}
                      />
                    </TableCell>
                    <TableCell
                      className="border-b"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              removeDocument(doc.sourceDocument.id)
                            }
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Document From Sample
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      {analytics !== undefined && (
        <AnalyticsInputRefusalsNote
          targets={analytics.targets}
          documents={auditSession.documents}
          sourceRows={auditSession.sourceRows}
          documentNames={documentNames}
          feedsState={analytics.feedsState}
        />
      )}
      <div className="mt-3 flex justify-between gap-2 p-1">
        <div className="relative">
          <Input
            type="file"
            accept={getFileInputAcceptString() + ",.zip"}
            multiple
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              const validFiles = await validateFilesWithToast(files);
              if (validFiles.length === 0) return;
              handleFileUpload(validFiles);
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="sample-upload-documents-input"
          />
          <Button
            variant="outline"
            className="pointer-events-none"
            loading={isUploading}
          >
            Upload Documents To This Sample
          </Button>
        </div>
      </div>

      <UploadProgressOverlay
        state={presignedUploadState}
        onCancel={cancelPresignedUpload}
      />
    </div>
  );
};
